import Big from 'big.js'
import times from 'lodash/times'
import React, {
  type ChangeEvent,
  type ComponentProps,
  useCallback,
  useMemo,
} from 'react'

import { warnInDev } from '../../../util/logging'
import type { InternalInput } from './InternalInput'

const KEY_REMAPPING: { [rawKey: string]: string } = {
  // Some older browsers produce arrow key names without the leading 'Arrow'.
  Down: 'ArrowDown',
  // This can be produced by the numpad on keyboards.
  Separator: '.',
  // This can be produced by the numpad on keyboards.
  Subtract: '-',
  Up: 'ArrowUp',
}

/**
 * This hook allows you to turn any InternalInput into an input that works well for numbers.
 *
 * For a description of each argument, see the docs for `NumberInput`.
 */
export function useNumberInput({
  addTrailingZeros = false,
  componentName,
  enableNumberSeparators = false,
  max: maxProp,
  maxDecimalPlaces,
  min: minProp,
  onBlur: onBlurProp,
  onChange: onChangeProp,
  onKeyDown: onKeyDownProp,
  removeLeadingZeros = false,
  step: stepProp,
  textAlign = 'end',
  value,
  visualFormatting,
}: {
  componentName: string
  value: string
  onChange: (props: { value: string }) => void
  step?: string | 'none'
  min?: string
  max?: string
  maxDecimalPlaces?: number
  addTrailingZeros?: boolean
  removeLeadingZeros?: boolean
  enableNumberSeparators?: boolean
  textAlign?: 'start' | 'end'
  visualFormatting?: {
    add: (value: string) => string
    remove: (value: string) => string
  }
  onBlur?: ComponentProps<typeof InternalInput>['onBlur']
  onKeyDown?: ComponentProps<typeof InternalInput>['onKeyDown']
}): {
  onBlur: ComponentProps<typeof InternalInput>['onBlur']
  onChange: ComponentProps<typeof InternalInput>['onChange']
  onKeyDown: ComponentProps<typeof InternalInput>['onKeyDown']
  type: ComponentProps<typeof InternalInput>['type']
  value: ComponentProps<typeof InternalInput>['value']
  overrideInputStyles: ComponentProps<
    typeof InternalInput
  >['overrideInputStyles']
} {
  // The add/remove methods are used inside a useCallback, so we memoize them to reduce churn.
  const addVisualFormatting = useMemo(
    () => visualFormatting?.add ?? ((val: string) => val),
    [visualFormatting]
  )
  const removeVisualFormatting = useMemo(
    () => visualFormatting?.remove ?? ((val: string) => val),
    [visualFormatting]
  )
  const runAfterUpdate = useRunAfterUpdate()

  // FIXME: This may lead to issues further down the road
  // The 'step' logical expression could make the dependencies of useCallback Hook (at line 283) change on every render. To fix this, wrap the initialization of 'step' in its own useMemo() Hook
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const step: 'none' | Big =
    stepProp === 'none' ? 'none' : convertToBig(stepProp) || new Big(1)
  const min = convertToBig(minProp) || undefined
  const max = convertToBig(maxProp) || undefined

  ;[
    { propName: 'step', propValue: stepProp },
    { propName: 'min', propValue: minProp },
    { propName: 'max', propValue: maxProp },
  ].forEach(({ propName, propValue }) => {
    if (propName === 'step' && propValue === 'none') return

    if (
      propValue !== undefined &&
      (propValue === '' || !isValidNumber(propValue))
    ) {
      warnInDev(
        `${componentName}: provided ${propName} (${propValue}) is not a valid number`
      )
    }
  })

  if (min !== undefined && max !== undefined && min.gt(max)) {
    warnInDev(
      `${componentName}: min (${min}) should be greater than max (${max})`
    )
  }

  if (stepProp !== undefined && step !== 'none' && step.lte(0)) {
    warnInDev(
      `${componentName}: step (${stepProp}) should be a positive non-zero number to avoid user confusion`
    )
  }
  if (
    maxDecimalPlaces !== undefined &&
    (maxDecimalPlaces < 0 || maxDecimalPlaces !== Math.floor(maxDecimalPlaces))
  ) {
    warnInDev(
      `${componentName}: maxDecimalPlaces (${maxDecimalPlaces}) should be a non-negative integer`
    )
  }

  if (maxDecimalPlaces === 0) {
    ;[
      { propName: 'step', propValue: step },
      { propName: 'min', propValue: min },
      { propName: 'max', propValue: max },
    ].forEach(({ propName, propValue }) => {
      if (
        maxDecimalPlaces === 0 &&
        propValue !== undefined &&
        propValue !== 'none' &&
        !propValue.eq(propValue.round())
      ) {
        warnInDev(
          `${componentName}: maxDecimalPlaces is set to 0 but provided ${propName} (${propValue}) is not an integer`
        )
      }
    })
  }

  const convertedValue = convertToBig(value)
  if (convertedValue) {
    if (value !== '' && min !== undefined && convertedValue.lt(min)) {
      warnInDev(
        `${componentName}: provided value (${value}) should be >= min (${min})`
      )
    }
    if (value !== '' && max !== undefined && convertedValue.gt(max)) {
      warnInDev(
        `${componentName}: provided value (${value}) should be <= max (${max})`
      )
    }
    if (maxDecimalPlaces === 0) {
      if (
        value !== '' &&
        convertToBig(value)?.toString() !== convertToInteger(value)
      ) {
        warnInDev(
          `${componentName}: maxDecimalPlaces is set to 0 but provided value (${value}) is not an integer`
        )
      }
    }
  }

  // We keep track of the rendered value locally so that we can do normalization passes on it
  // before providing it to the parent via onChange. This also allows us to store a version of
  // the value internally that we don't want to pass to the parent, for when the value is
  // invalid (e.g. `'10.'` or larger than `max`).
  const [renderedValue, setRenderedValue] = React.useState('')
  // When the `value` prop changes, and on initial render, update our local copy so that we
  // actually update the underlying input.
  React.useEffect(
    () => {
      const valueToUse =
        maxDecimalPlaces === 0 ? convertToInteger(value) : value
      if (convertToBig(valueToUse)) {
        setRenderedValue(
          _addNumberSeparators(enableNumberSeparators, valueToUse)
        )
      } else {
        setRenderedValue('')
      }
      // Ensure that the parent is in sync with what we're showing to the user.
      if (valueToUse !== value) {
        onChangeProp({ value: valueToUse })
      }
    },
    // We don't want to re-run this useEffect if onChangeProp changes.
    // NOTE (david.aghassi) this was disabled during the upgrade of eslint-plugin-react-hooks.
    // Or was a pre-existing offense
    // Please refactor and solve the issue next time this code is touched
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxDecimalPlaces, value, enableNumberSeparators]
  )

  // We wrap the provided `onChange` callback to first do some basic formatting and validation
  // on the input value before deciding whether or not to call the provided `onChange`.
  const onChange = useCallback(
    ({
      event,
      value: valueAfterUserChange,
    }: {
      value: string
      event: ChangeEvent<HTMLInputElement>
    }) => {
      const unformattedValue = removeVisualFormatting(valueAfterUserChange)
      const inputEl = event.target
      const cursorValue = inputEl.selectionStart
      // We pass in false for removeLeadingZeros here because we don't want to restrict input as users type
      const nextNormalizedValue = normalizeForRender(
        unformattedValue,
        maxDecimalPlaces,
        false
      )
      const nextRenderedValue = _addNumberSeparators(
        enableNumberSeparators,
        nextNormalizedValue
      )
      setRenderedValue(nextRenderedValue)

      // manually modify cursor value if warranted (cursor not at the end of input, change was not an invalid insertion/deletion, and there is a non-null cursor value)
      if (
        enableNumberSeparators &&
        cursorValue !== valueAfterUserChange.length &&
        renderedValue !== nextRenderedValue &&
        cursorValue !== null
      ) {
        // we need the fully rendered value with all characters present so we can accurately determine the correct cursor position
        const fullyRenderedValue = addVisualFormatting(nextRenderedValue)
        let modifiedCursorValue = getAdjustedCursorValue({
          newValue: fullyRenderedValue,
          oldCursorValue: cursorValue,
          oldValue: valueAfterUserChange,
        })
        // if cursor would land on a number separator, just move it one position to the left to avoid user deleting the number separator
        if (fullyRenderedValue[modifiedCursorValue - 1] === ',') {
          modifiedCursorValue -= 1
        }
        runAfterUpdate(() => {
          inputEl.setSelectionRange(modifiedCursorValue, modifiedCursorValue)
        })
      }

      if (
        nextNormalizedValue === '' ||
        (isValidNumber(nextNormalizedValue) &&
          isInRange(nextNormalizedValue, min, max))
      ) {
        onChangeProp({ value: nextNormalizedValue })
      }
    },
    [
      renderedValue,
      runAfterUpdate,
      removeVisualFormatting,
      addVisualFormatting,
      maxDecimalPlaces,
      min,
      max,
      onChangeProp,
      enableNumberSeparators,
    ]
  )

  // Because we don't call the onChange prop with interim values that may be invalid (e.g.
  // '10.' or larger than the specified max), we add an onBlur handler that does a final
  // pass of normalizing the value to a valid number and calls onChange with that value if
  // it is different from the previous call.
  const onBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      // If renderedValue is the empty string, then we already called onChange with it, so
      // we don't need to do so again.
      if (renderedValue !== '') {
        // Generate the final value, and if it's different from what we'd been showing, update
        // ourselves and call onChange one last time.
        const finalValue = fullyNormalize(renderedValue, {
          addTrailingZeros,
          max,
          maxDecimalPlaces,
          min,
          removeLeadingZeros,
        })
        if (finalValue !== renderedValue) {
          onChangeProp({ value: finalValue })
        }
        setRenderedValue(
          _addNumberSeparators(enableNumberSeparators, finalValue)
        )
      }

      // If we were provided an onBlur callback for the input, call it now, after we've
      // potentially called onChange one last time.
      onBlurProp?.(event)
    },
    [
      renderedValue,
      onBlurProp,
      min,
      max,
      maxDecimalPlaces,
      addTrailingZeros,
      removeLeadingZeros,
      enableNumberSeparators,
      onChangeProp,
    ]
  )

  // We add a keydown event handler in order to manually emulate the native `<input type="number"`
  // element's ability to modify the value using the arrow keys. On up/down arrow keys, we
  // increment/decrement the value and call `onChange`.
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // If we were provided an onKeyDown callback for the input, call it first.
      onKeyDownProp?.(event)

      const key = KEY_REMAPPING[event.key] || event.key
      if (key !== 'ArrowUp' && key !== 'ArrowDown') {
        return
      }
      if (step === 'none') {
        return
      }

      let nextValue: string
      const currValue = convertToBig(value)
      if (currValue && key === 'ArrowDown') {
        nextValue = roundToStep(currValue.minus(step), step, 'up').toString()
      } else if (currValue && key === 'ArrowUp') {
        nextValue = roundToStep(currValue.plus(step), step, 'down').toString()
      } else if (key === 'ArrowDown') {
        nextValue = min ? min.toString() : step.times(-1).toString()
      } else {
        nextValue = min ? min.toString() : step.toString()
      }

      const finalValue = fullyNormalize(nextValue, {
        addTrailingZeros,
        max,
        maxDecimalPlaces,
        min,
        removeLeadingZeros,
      })

      setRenderedValue(finalValue)
      onChangeProp({ value: finalValue })
    },
    [
      onKeyDownProp,
      min,
      value,
      max,
      maxDecimalPlaces,
      addTrailingZeros,
      removeLeadingZeros,
      onChangeProp,
      step,
    ]
  )

  return {
    onBlur,
    onChange,
    onKeyDown,
    overrideInputStyles: {
      // Not all browsers support 'start' and 'end', so we provide 'left' and 'right'
      // as fallbacks. This will need to be modified when we support RTL languages.
      textAlign: textAlign === 'start' ? ['left', 'start'] : ['right', 'end'],
    },
    type: 'text',
    value: addVisualFormatting(renderedValue),
  }
}

/**
 * Returns an integer derived from the provided `value`.
 */
function convertToInteger(str: string): string {
  // NOTE(eric.lovett): This currently rounds down for any positive value and up for any
  // negative value, which may result in surprising UX.
  return str.split('.')[0]
}

/**
 * Returns the number of decimal places that should be used as a default maximum for the
 *  provided `value`, for use if the parent didn't provide a value for `maxDecimalPlaces`.
 */
function getDefaultMaxDecimalPlaces(value: string): number {
  const valueAsBig = convertToBig(value)
  return !valueAsBig ||
    valueAsBig.eq(0) ||
    valueAsBig.lte(-1) ||
    valueAsBig.gte(1)
    ? 2
    : 4
}

/**
 * Rounds the provided `value` up or down, as specified, to the nearest multiple of the provided `step`.
 */
function roundToStep(num: Big, step: Big, direction: 'up' | 'down'): Big {
  let roundingMode: 0 | 3
  if (num.gte(0)) {
    roundingMode = direction === 'up' ? 3 : 0
  } else {
    roundingMode = direction === 'up' ? 0 : 3
  }

  return (
    num
      .div(step)
      // This is the big.js equivalent of Math.ceil (for 'up') and Math.floor (for 'down').
      .round(0, roundingMode)
      .times(step)
  )
}

/**
 * Returns a boolean indicating whether or not the provided value is a valid number.
 */
function isValidNumber(value: string): boolean {
  return convertToBig(value) !== null
}

/**
 * Returns the provided value as a Big after stripping out all non numeric characters, or null if the value is not a valid number.
 */
export function convertToBig(value: string | undefined): Big | null {
  if (value === undefined || value.endsWith('.')) {
    return null
  }

  try {
    return new Big(value.replace(/[^\-.0-9]/g, ''))
  } catch (err) {
    return null
  }
}

/**
 * Returns a boolean indicating whether or not the provided value is between the min and
 * max, if either is provided.
 */
function isInRange(
  value: string,
  min: Big | undefined,
  max: Big | undefined
): boolean {
  const v = new Big(value)
  return (min === undefined || v.gte(min)) && (max === undefined || v.lte(max))
}

/**
 * Returns a boolean indicating whether or not the provided value is negative.
 */
function isNegative(value: string): boolean {
  // NOTE(eric.lovett): Note for i18n that this may not work for all locales.
  return value.trim().startsWith('-')
}

/**
 * Takes in a string representing a number (must already be fully normalized to avoid double formatting)
 * Returns a string with number separators in it (if applicable)
 * Exported for testing purposes only.
 */
export function _addNumberSeparators(
  enableNumberSeparators: boolean,
  value: string
): string {
  if (!enableNumberSeparators) {
    return value
  }

  // for consistency we do not format numbers with leading zeros
  const hasLeadingZeroes =
    value !== '0' && value.length > 0 && value.charAt(0) === '0'
  if (value.length === 0 || hasLeadingZeroes) {
    return value
  }

  if (!value.includes('.')) {
    return _addCommas(value)
  }

  const [integerPart, decimalPart] = value.split('.')

  return `${_addCommas(integerPart)}.${decimalPart}`
}

/**
 * Takes in a string representing an integer (with no separators already added)
 * Returns a string with number separators in it (if applicable)
 */
function _addCommas(value: string): string {
  if (value.length <= 3) {
    return value
  }
  const diff = value.length % 3
  let toRet = value.substring(0, diff)
  for (let i = diff; i < value.length; i += 3) {
    if (i === 0) {
      toRet += `${value.substring(i, i + 3)}`
    } else {
      toRet += `,${value.substring(i, i + 3)}`
    }
  }
  return toRet
}

/**
 * Returns a normalized version of `value` that can be rendered to the user, but isn't
 * necessarily a properly formatted number (e.g. '1000.').
 */
function normalizeForRender(
  value: string,
  maxDecimalPlaces: number | undefined,
  removeLeadingZeros: boolean
): string {
  const valueIsNegative = isNegative(value)

  // Remove leading zeros, but don't remove 0's if the number is simply '0'
  // The first regex expression matches 1 or more zeroes that are not at the end of the string
  const trimmed = value.trim()
  const digitsAndDecimals = removeLeadingZeros
    ? trimmed.replace(/^0+(?!$)/, '').replace(/[^.0-9]/g, '')
    : trimmed.replace(/[^.0-9]/g, '')
  const signChar = valueIsNegative ? '-' : ''

  // If we know we only want integers, return one now.
  if (maxDecimalPlaces === 0) {
    return convertToInteger(`${signChar}${digitsAndDecimals}`)
  }

  // If we have an integer, we shouldn't try to format a fractional part.
  if (!digitsAndDecimals.includes('.')) {
    return `${signChar}${digitsAndDecimals}`
  }

  // We want to return a value that has a fractional part, so format it.
  const [integerPart, fractionalPart] = digitsAndDecimals.split('.')
  let normalizedValue = `${signChar}${integerPart || '0'}.`
  const maxNumDecimals =
    maxDecimalPlaces || getDefaultMaxDecimalPlaces(digitsAndDecimals)
  if (fractionalPart.length > maxNumDecimals) {
    // Use Big here to make sure we round the fractional part correctly.
    normalizedValue += new Big(`0.${fractionalPart}`)
      .toFixed(maxNumDecimals)
      .split('.')[1]
  } else {
    normalizedValue += fractionalPart
  }
  return normalizedValue
}

/**
 * Returns a fully normalized version of `value` that represents a valid number given all the
 * constraints.
 */
function fullyNormalize(
  value: string,
  {
    addTrailingZeros,
    max,
    maxDecimalPlaces,
    min,
    removeLeadingZeros,
  }: {
    min: Big | undefined
    max: Big | undefined
    maxDecimalPlaces: number | undefined
    addTrailingZeros: boolean
    removeLeadingZeros: boolean
  }
): string {
  const valueAsBig = convertToBig(value)
  if (!valueAsBig) {
    return ''
  }

  // First clamp the value to our min and max.
  let clampedValue = value
  if (min !== undefined && valueAsBig.lt(min)) {
    clampedValue = min.toString()
  } else if (max !== undefined && valueAsBig.gt(max)) {
    clampedValue = max.toString()
  }

  // Then normalize the formatting of it.
  let normalizedValue = normalizeForRender(
    clampedValue,
    maxDecimalPlaces,
    removeLeadingZeros
  )

  // If we know we only want integers, return one now.
  if (maxDecimalPlaces === 0) {
    return convertToInteger(normalizedValue)
  }

  if (normalizedValue.endsWith('.')) {
    normalizedValue = normalizedValue.slice(0, -1)
  }

  if (!addTrailingZeros) {
    return normalizedValue
  }

  const numDecimalsRequired =
    maxDecimalPlaces || getDefaultMaxDecimalPlaces(clampedValue)
  const fractionalPart = normalizedValue.split('.')[1] || ''
  const numZerosToAdd = numDecimalsRequired - fractionalPart.length
  if (numZerosToAdd <= 0) {
    return normalizedValue
  }

  const zerosToAdd = times(numZerosToAdd, () => '0').join('')
  return `${normalizedValue}${fractionalPart ? '' : '.'}${zerosToAdd}`
}

function useRunAfterUpdate() {
  const afterPaintRef = React.useRef<(() => void) | null>(null)
  React.useLayoutEffect(() => {
    if (afterPaintRef.current) {
      afterPaintRef.current()
      afterPaintRef.current = null
    }
  })
  const runAfterUpdate = (fn: () => void) => {
    afterPaintRef.current = fn
  }
  return runAfterUpdate
}

function getAdjustedCursorValue({
  newValue,
  oldCursorValue,
  oldValue,
}: {
  oldCursorValue: number
  oldValue: string
  newValue: string
}) {
  const COMMA_REGEX = /,/g
  const oldCommaCount = (
    oldValue.slice(0, oldCursorValue).match(COMMA_REGEX) || []
  ).length
  const newCommaCount = (
    newValue.slice(0, oldCursorValue).match(COMMA_REGEX) || []
  ).length
  return oldCursorValue + (newCommaCount - oldCommaCount)
}
